import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { ReactNode } from "react";
import { useFragment } from "react-relay";

import { VStack } from "@whoosh/design-system/src/components/primitive";

import { BookingOwner } from "../../BookingOwner/BookingOwner";
import { CancelBookingButton } from "../../CancelBooking/CancelBookingButton";
import { EditBookingButton } from "../../EditBookingButton/EditBookingButton";
import { JoinBookingButton } from "../../JoinBookingButton/JoinBookingButton";
import { MoveBookingButton } from "../../MoveBookingButton/MoveBookingButton";
import { MemberNoteToTheStaff } from "./RoundList/MemberNoteToTheStaff";
import { BookingActionsSection_Booking$key } from "./__generated__/BookingActionsSection_Booking.graphql";

type Props = {
  data: BookingActionsSection_Booking$key | null;
  onSaveBookingNote: (notes: string) => void;
  isAnyDynamicRateOnBookingLocked: boolean;
  isLockedRequest: boolean;
  isRequestPresentation: boolean;
  currentSessionUserClientId: string;
  currentSessionMemberShipTypeId: string;
  recurringRequestActions?: ReactNode;
};

export const BookingActionsSection = ({
  data,
  onSaveBookingNote,
  isAnyDynamicRateOnBookingLocked,
  isLockedRequest,
  isRequestPresentation,
  currentSessionUserClientId,
  currentSessionMemberShipTypeId,
  recurringRequestActions,
}: Props) => {
  const bookingData = useFragment(BookingActionsSectionFragmentString, data);

  if (!bookingData) return null;

  return (
    <>
      {!isLockedRequest && !isRequestPresentation ? (
        <JoinBookingButton bookingRef={bookingData} />
      ) : null}
      {!isLockedRequest ? (
        <EditBookingButton
          bookingRef={bookingData}
          currentSessionMemberShipTypeId={currentSessionMemberShipTypeId}
          currentSessionUserClientId={currentSessionUserClientId}
        />
      ) : null}

      {recurringRequestActions}

      {!isLockedRequest ? (
        <MemberNoteToTheStaff
          buttonText={
            bookingData?.notesFromMember?.length ? t`Edit Note` : t`Add Note`
          }
          placeHolderText={t`Provide any information that you would like the staff to be aware of`}
          notes={bookingData?.notesFromMember ?? ""}
          onSave={onSaveBookingNote}
          canUpdate={bookingData?.canUpdate}
        />
      ) : null}
      <VStack gap={2}>
        {isLockedRequest ||
        isRequestPresentation ||
        isAnyDynamicRateOnBookingLocked ? null : (
          <MoveBookingButton
            bookingRef={bookingData}
            currentSessionUserClientId={currentSessionUserClientId}
          />
        )}
        {!isLockedRequest ? (
          <CancelBookingButton
            bookingRef={bookingData}
            currentSessionUserClientId={currentSessionUserClientId}
          />
        ) : null}
        <BookingOwner bookingOwnerRef={bookingData} />
      </VStack>
    </>
  );
};

export const BookingActionsSectionFragmentString = graphql`
  fragment BookingActionsSection_Booking on Booking {
    id
    notesFromMember
    canUpdate
    ...JoinBookingButton_Booking
    ...CancelBookingButton_Booking
    ...MoveBookingButton_Booking
    ...EditBookingButton_Booking
    ...BookingOwner_Booking
  }
`;
